import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * Omega release dossier and final production sign-off (part 60).
 *
 * Every row on this page is evidence the server measured: the live HTTP probes, the latest
 * readiness run read back from the database, the migration head and the captured verify.sh
 * summary. The page cannot make a failed item pass, and the sign-off button is disabled when any
 * item failed or any required item is not green. After sign-off the dossier is sealed; this screen
 * then shows the named approver, the role and the immutable evidence hash.
 *
 * The page is not routed from App.tsx in this part. The API mount is also intentionally unmounted;
 * both are one-line wiring changes reviewed with the first operator who needs the console.
 */

type Status = 'pass' | 'warn' | 'fail'
type Kind = 'live_probe' | 'readiness' | 'migration' | 'verify'

interface Item {
  id: string
  tenant_id: string
  dossier_id: string
  item_id: string
  kind: Kind
  title_th: string
  status: Status
  required: boolean
  evidence_th: string
  source: string
  observed_at: string
  digest: string
  position: number
  created_at: string
}

interface Dossier {
  id: string
  tenant_id: string
  target_url: string
  release_label: string
  git_commit: string
  generated_at: string
  summary_th: string
  evidence_hash: string
  probe_total: number
  probe_passed: number
  probe_failed: number
  items_total: number
  items_required: number
  items_failed: number
  readiness_found: boolean
  readiness_run_id?: string
  readiness_score: number
  readiness_verdict: string
  migration_head: string
  migration_expected: string
  migrations_applied: number
  verify_present: boolean
  verify_summary_path: string
  verify_passed: number
  verify_failed: number
  signed_off: boolean
  signed_off_at?: string
  signed_off_by?: string
  signed_off_name: string
  signed_off_role: string
  sign_off_note_th: string
  sign_off_digest: string
  created_by?: string
  created_by_role: string
  created_at: string
}

interface Detail {
  dossier: Dossier
  items: Item[]
  hash_verified: boolean
  recomputed_hash: string
  counters_agree: boolean
  immutable: boolean
  sign_off_verified: boolean
}

interface ExportArtefact {
  format: string
  exported_at: string
  dossier: Dossier
  items: Item[]
  evidence_hash: string
  hash_verified: boolean
  counters_agree: boolean
  immutable: boolean
  sign_off?: {
    approver_name: string
    role: string
    note_th?: string
    signed_at: string
    evidence_hash: string
    digest: string
  }
}

const STATUS_LABEL: Record<Status, string> = {
  pass: 'ผ่าน',
  warn: 'ควรปรับปรุง',
  fail: 'ไม่ผ่าน',
}

const STATUS_TONE: Record<Status, string> = {
  pass: 'var(--color-emerald-soft)',
  warn: 'var(--color-gold)',
  fail: 'var(--color-crimson)',
}

const KIND_LABEL: Record<Kind, string> = {
  live_probe: 'ตรวจโฮสต์จริง',
  readiness: 'ความพร้อม',
  migration: 'migration',
  verify: 'ชุดตรวจสอบ',
}

const VERDICT_LABEL: Record<string, string> = {
  ready: 'พร้อมใช้งาน',
  conditional: 'พร้อมแบบมีเงื่อนไข',
  not_ready: 'ยังไม่พร้อม',
  blocked: 'ถูกบล็อก',
  '': 'ยังไม่มีผลประเมิน',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function hostOf(target: string): string {
  try {
    return new URL(target).host
  } catch {
    return target
  }
}

function shortHash(value: string): string {
  if (value.length <= 20) return value
  return `${value.slice(0, 12)}…${value.slice(-8)}`
}

function scoreTone(score: number): string {
  if (score >= 90) return 'var(--color-emerald)'
  if (score >= 70) return 'var(--color-gold)'
  return 'var(--color-crimson)'
}

function thaiRole(role: string): string {
  switch (role) {
    case 'owner':
      return 'เจ้าขององค์กร'
    case 'admin':
      return 'ผู้ดูแลระบบ'
    case 'audit_manager':
      return 'ผู้จัดการตรวจสอบ'
    case 'auditor':
      return 'ผู้ตรวจสอบ'
    case 'reviewer':
      return 'ผู้สอบทาน'
    case 'client_viewer':
      return 'ผู้ชมฝั่งลูกค้า'
    default:
      return role || 'ไม่ระบุ'
  }
}

export function Release() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canWrite = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'

  const [selectedId, setSelectedId] = useState('')
  const [label, setLabel] = useState('')
  const [approver, setApprover] = useState('')
  const [note, setNote] = useState('')
  const [notice, setNotice] = useState('')

  const history = useQuery({
    queryKey: ['release-dossiers'],
    queryFn: () => api<Dossier[]>('/v1/release/dossiers?limit=30'),
  })

  const activeId = selectedId || history.data?.[0]?.id || ''
  const detail = useQuery({
    queryKey: ['release-dossier', activeId],
    queryFn: () => api<Detail>(`/v1/release/dossiers/${activeId}`),
    enabled: activeId !== '',
    retry: false,
  })

  const build = useMutation({
    mutationFn: () => apiWrite<Detail>('/v1/release/dossiers', 'POST', {
      release_label: label.trim(),
    }),
    onSuccess: (saved) => {
      setSelectedId(saved.dossier.id)
      setNotice(`สร้าง dossier แล้ว · ${saved.dossier.items_total} รายการ · ${saved.dossier.summary_th}`)
      qc.setQueryData(['release-dossier', saved.dossier.id], saved)
      void qc.invalidateQueries({ queryKey: ['release-dossiers'] })
    },
    onError: (err) => setNotice(errorText(err, 'สร้าง dossier ไม่สำเร็จ')),
  })

  const signOff = useMutation({
    mutationFn: () => apiWrite<Detail>(`/v1/release/dossiers/${activeId}/sign-off`, 'POST', {
      approver_name: approver.trim(),
      note_th: note.trim(),
    }),
    onSuccess: (saved) => {
      setNotice(`ลงนามปิดรุ่นโดย ${saved.dossier.signed_off_name} · แฮชหลักฐานคงเดิม`)
      setApprover('')
      setNote('')
      qc.setQueryData(['release-dossier', saved.dossier.id], saved)
      void qc.invalidateQueries({ queryKey: ['release-dossiers'] })
    },
    onError: (err) => setNotice(errorText(err, 'ลงนามปิดรุ่นไม่สำเร็จ')),
  })

  const exportJSON = useMutation({
    mutationFn: async () => {
      const artefact = await api<ExportArtefact>(`/v1/release/dossiers/${activeId}/export`)
      const blob = new Blob([JSON.stringify(artefact, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `auditai-release-${artefact.dossier.id}.json`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
      return artefact
    },
    onSuccess: () => setNotice('ส่งออกหลักฐาน JSON แล้ว · ไฟล์นี้เก็บคู่กับรุ่นที่ติดตั้ง'),
    onError: (err) => setNotice(errorText(err, 'ส่งออกหลักฐานไม่สำเร็จ')),
  })

  const current = detail.data?.dossier ?? null
  const items = detail.data?.items ?? []
  const loading = history.isLoading || detail.isLoading
  const requiredOpen = useMemo(
    () => items.filter((item) => item.required && item.status !== 'pass'),
    [items],
  )
  const blockingReason = current
    ? current.items_failed > 0
      ? `มีรายการไม่ผ่าน ${current.items_failed} รายการ จึงลงนามไม่ได้`
      : requiredOpen.length > 0
        ? `รายการบังคับยังไม่ผ่าน ${requiredOpen.length} รายการ`
        : ''
    : ''
  const canSign = Boolean(current) && canWrite && !current?.signed_off && blockingReason === '' && approver.trim().length >= 3

  return (
    <>
      <PageHead
        title="ทะเบียนส่งมอบรุ่นสุดท้าย"
        sub="รวมหลักฐานจากโฮสต์จริง ผลประเมินความพร้อม หัว migration และชุดตรวจสอบไว้ในเอกสารเดียว · ลงนามได้เมื่อไม่มีรายการใดไม่ผ่าน"
        actions={(
          <div className="row" style={{ gap: 8 }}>
            {current ? (
              <button className="btn" onClick={() => exportJSON.mutate()} disabled={exportJSON.isPending}>
                {exportJSON.isPending ? 'กำลังส่งออก…' : 'ส่งออก JSON'}
              </button>
            ) : null}
            {canWrite ? (
              <button className="btn btn-primary" onClick={() => { setNotice(''); build.mutate() }} disabled={build.isPending}>
                {build.isPending ? 'กำลังเก็บหลักฐาน…' : 'สร้าง dossier ใหม่'}
              </button>
            ) : null}
          </div>
        )}
      />

      {canWrite ? (
        <div className="card card-pad" style={{ marginBottom: 16 }}>
          <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 320px' }}>
              <div className="card-label" style={{ marginBottom: 6 }}>ชื่อรุ่นที่ส่งมอบ</div>
              <input
                className="input"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="เว้นว่างเพื่อให้ระบบตั้งชื่อจากวันที่และเวลา"
                aria-label="ชื่อรุ่นที่ส่งมอบ"
              />
            </div>
            <span className="dim" style={{ fontSize: 12 }}>
              ระบบจะเรียกโฮสต์จริง อ่านผลประเมินล่าสุดจากฐานข้อมูล และอ่านหัว migration ทุกครั้ง
            </span>
          </div>
        </div>
      ) : null}

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 16 }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {loading ? (
        <div className="stack"><Skel h={90} /><Skel h={220} /></div>
      ) : current === null ? (
        <div className="card card-pad">
          <Empty
            title="ยังไม่มี dossier การส่งมอบ"
            body="สร้าง dossier ครั้งแรก ระบบจะวัดผลจากโฮสต์จริงและฐานข้อมูล แล้วบันทึกหลักฐานทุกข้อพร้อมแฮชที่ตรวจซ้ำได้"
          />
          {canWrite ? (
            <div className="row" style={{ justifyContent: 'center', marginTop: 16 }}>
              <button className="btn btn-primary" onClick={() => build.mutate()} disabled={build.isPending}>
                {build.isPending ? 'กำลังเก็บหลักฐาน…' : 'สร้าง dossier แรก'}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="grid-4" style={{ marginBottom: 16 }}>
            <Stat
              label="รายการหลักฐาน"
              value={current.items_total}
              unit="รายการ"
              foot={`ผ่าน ${current.items_total - current.items_failed - items.filter((item) => item.status === 'warn').length} · ไม่ผ่าน ${current.items_failed} · บังคับ ${current.items_required}`}
            />
            <Stat
              label="ความพร้อมล่าสุด"
              value={<span className="tnum" style={{ color: scoreTone(current.readiness_score) }}>{current.readiness_found ? current.readiness_score : '—'}</span>}
              unit={current.readiness_found ? '/100' : undefined}
              foot={current.readiness_found ? `${VERDICT_LABEL[current.readiness_verdict] ?? current.readiness_verdict} · ${current.readiness_run_id ? current.readiness_run_id.slice(0, 8) : ''}` : 'ยังไม่มีผลประเมินในฐานข้อมูล'}
            />
            <Stat
              label="ตรวจโฮสต์จริง"
              value={`${current.probe_passed}/${current.probe_total}`}
              foot={`ล้มเหลว ${current.probe_failed} · ${hostOf(current.target_url)}`}
            />
            <Stat
              label="ชุดตรวจสอบ verify.sh"
              value={current.verify_present ? `${current.verify_passed}/${current.verify_passed + current.verify_failed}` : 'ไม่มีไฟล์'}
              foot={current.verify_present ? `FAIL=${current.verify_failed}` : 'รายการนี้เป็นคำเตือน ไม่ใช่การผ่าน'}
            />
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <div className="card-label">รุ่นที่ส่งมอบ</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginTop: 6 }}>{current.release_label}</div>
                <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
                  {hostOf(current.target_url)} · สร้าง {thaiDateTime(current.generated_at)} · โดย {thaiRole(current.created_by_role)}
                  {current.git_commit ? ` · commit ${current.git_commit}` : ''}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="card-label">สถานะเอกสาร</div>
                <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                  <span className="chip" style={{ color: current.signed_off ? 'var(--color-emerald-soft)' : 'var(--color-gold)' }}>
                    {current.signed_off ? 'ลงนามแล้ว' : 'ยังไม่ลงนาม'}
                  </span>
                  <span className="chip">
                    migration {current.migration_head || '—'} / ต้องมี {current.migration_expected}
                  </span>
                </div>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>{current.summary_th}</div>
              </div>
            </div>
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
              <div className="card-label">แฮชของหลักฐานทั้งชุด</div>
              <div className="row" style={{ gap: 8 }}>
                <span className="chip" style={{ color: detail.data?.hash_verified ? 'var(--color-emerald-soft)' : 'var(--color-crimson)' }}>
                  {detail.data?.hash_verified ? 'แฮชตรงกับรายการ' : 'แฮชไม่ตรง'}
                </span>
                <span className="chip" style={{ color: detail.data?.counters_agree ? 'var(--color-emerald-soft)' : 'var(--color-crimson)' }}>
                  {detail.data?.counters_agree ? 'ตัวนับตรงกับแถว' : 'ตัวนับไม่ตรง'}
                </span>
                {current.signed_off ? (
                  <span className="chip" style={{ color: detail.data?.sign_off_verified ? 'var(--color-emerald-soft)' : 'var(--color-crimson)' }}>
                    {detail.data?.sign_off_verified ? 'ตราลงนามสมบูรณ์' : 'ตราลงนามไม่ตรง'}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="mono" style={{ fontSize: 12.5, wordBreak: 'break-all' }}>{current.evidence_hash}</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
              sha256 ของหัวเอกสารและทุกรายการตามลำดับ · ตรวจซ้ำได้จากฐานข้อมูลโดยไม่ต้องเชื่อหน้าจอ
              {current.signed_off ? ' · เอกสารปิดผนึกหลังลงนามแล้ว' : ''}
            </div>
          </div>

          {blockingReason ? (
            <div className="card card-pad" style={{ marginBottom: 16, borderLeft: '3px solid var(--color-crimson)' }}>
              <div className="card-label" style={{ color: 'var(--color-crimson)' }}>ยังลงนามไม่ได้</div>
              <div className="dim" style={{ fontSize: 12.5, marginTop: 6 }}>{blockingReason}</div>
            </div>
          ) : null}

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="card-label" style={{ marginBottom: 10 }}>หลักฐานทุกข้อ</div>
            <div className="table-wrap" style={{ overflowX: 'auto' }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>สถานะ</th>
                    <th>รายการ</th>
                    <th>ประเภท</th>
                    <th>หลักฐานที่วัดได้</th>
                    <th>แหล่งที่มา</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className="sev" style={{ color: STATUS_TONE[item.status] }}>
                          <i className="sev-dot" style={{ background: STATUS_TONE[item.status] }} />
                          {STATUS_LABEL[item.status]}
                        </span>
                      </td>
                      <td>
                        <div>{item.title_th}</div>
                        <div className="dim mono" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {item.item_id}{item.required ? ' · บังคับ' : ' · ไม่บังคับ'}
                        </div>
                      </td>
                      <td className="dim" style={{ fontSize: 12 }}>{KIND_LABEL[item.kind] ?? item.kind}</td>
                      <td style={{ maxWidth: 560, whiteSpace: 'normal', fontSize: 12.5 }}>{item.evidence_th}</td>
                      <td className="dim mono" style={{ fontSize: 10.5, maxWidth: 260, whiteSpace: 'normal', wordBreak: 'break-all' }}>
                        {item.source || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <div className="card-label" style={{ marginBottom: 10 }}>
              {current.signed_off ? 'การลงนามปิดรุ่น' : 'ลงนามปิดรุ่น'}
            </div>
            {current.signed_off ? (
              <div className="stack" style={{ gap: 8 }}>
                <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
                  <span className="chip chip-on">{current.signed_off_name}</span>
                  <span className="chip">{thaiRole(current.signed_off_role)}</span>
                  <span className="chip">{current.signed_off_at ? thaiDateTime(current.signed_off_at) : ''}</span>
                </div>
                {current.sign_off_note_th ? (
                  <div className="dim" style={{ fontSize: 12.5 }}>{current.sign_off_note_th}</div>
                ) : null}
                <div className="dim mono" style={{ fontSize: 10.5, wordBreak: 'break-all' }}>
                  sign-off digest {current.sign_off_digest}
                </div>
                <div className="dim" style={{ fontSize: 11.5 }}>
                  หลักฐานถูกปิดผนึกแล้ว แฮชด้านบนต้องไม่เปลี่ยน · หากต้องการตรวจรอบใหม่ให้สร้าง dossier ใหม่
                </div>
              </div>
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 280px' }}>
                    <div className="card-label" style={{ marginBottom: 6 }}>ชื่อผู้อนุมัติ</div>
                    <input
                      className="input"
                      value={approver}
                      onChange={(event) => setApprover(event.target.value)}
                      placeholder="ชื่อ-นามสกุลจริงของผู้ลงนาม"
                      aria-label="ชื่อผู้อนุมัติ"
                      disabled={!canWrite}
                    />
                  </div>
                  <div style={{ flex: '2 1 360px' }}>
                    <div className="card-label" style={{ marginBottom: 6 }}>หมายเหตุการอนุมัติ</div>
                    <input
                      className="input"
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder="เช่น ตรวจหลักฐานครบทุกข้อแล้ว อนุมัติเปิดใช้งาน"
                      aria-label="หมายเหตุการอนุมัติ"
                      disabled={!canWrite}
                    />
                  </div>
                </div>
                <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <span className="dim" style={{ fontSize: 11.5 }}>
                    ลงนามได้เฉพาะ owner, admin และ audit_manager · บทบาทของคุณคือ {thaiRole(me?.role ?? '')}
                  </span>
                  <button
                    className="btn btn-primary"
                    onClick={() => { setNotice(''); signOff.mutate() }}
                    disabled={!canSign || signOff.isPending}
                  >
                    {signOff.isPending ? 'กำลังลงนาม…' : 'ลงนามปิดรุ่น'}
                  </button>
                </div>
                {!canSign && !current.signed_off ? (
                  <div className="dim" style={{ fontSize: 11.5 }}>
                    {blockingReason || (approver.trim().length < 3 ? 'กรอกชื่อผู้อนุมัติอย่างน้อย 3 ตัวอักษร' : 'บทบาทนี้ไม่มีอำนาจลงนาม')}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="card-label" style={{ marginBottom: 10 }}>ประวัติ dossier</div>
            {history.data && history.data.length > 0 ? (
              <div className="table-wrap" style={{ overflowX: 'auto' }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th>รุ่น</th>
                      <th className="num">รายการ</th>
                      <th className="num">ไม่ผ่าน</th>
                      <th className="num">ความพร้อม</th>
                      <th>สถานะ</th>
                      <th>แฮช</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.data.map((row) => (
                      <tr key={row.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedId(row.id)}>
                        <td className="dim tnum">{thaiDateTime(row.created_at)}</td>
                        <td>{row.release_label}</td>
                        <td className="num tnum">{row.items_total}</td>
                        <td className="num tnum" style={{ color: row.items_failed > 0 ? 'var(--color-crimson)' : undefined }}>{row.items_failed}</td>
                        <td className="num tnum">{row.readiness_found ? row.readiness_score : '—'}</td>
                        <td style={{ color: row.signed_off ? 'var(--color-emerald-soft)' : 'var(--color-gold)' }}>
                          {row.signed_off ? `ลงนาม · ${row.signed_off_name}` : 'ยังไม่ลงนาม'}
                        </td>
                        <td className="dim mono" style={{ fontSize: 10.5 }}>{shortHash(row.evidence_hash)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dim" style={{ fontSize: 12.5 }}>ยังไม่มีประวัติ</div>
            )}
          </div>
        </>
      )}

      {history.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <span className="dim" style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>
            {errorText(history.error, 'อ่านประวัติ dossier ไม่สำเร็จ')}
          </span>
        </div>
      ) : null}

      {detail.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <span className="dim" style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>
            {errorText(detail.error, 'อ่านรายละเอียด dossier ไม่สำเร็จ')}
          </span>
        </div>
      ) : null}
    </>
  )
}
